import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  X,
  ChevronDown,
  Calendar,
  Download,
  Filter,
  FileText,
  FileSpreadsheet,
  BarChart3,
  Package,
  RefreshCw,
  SortAsc,
  Database,
  CheckCircle,
  ChevronUp,
  ChevronRight,
  Building2,
  Store,
  TrendingUp,
  Hash,
  Grid,
  Printer,
  Plus,
  Minus,
  Info,
  Layers
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { reportsAPI, branchesAPI, chefsAPI } from '../services/api';
import { toast } from 'react-toastify';
import { format, startOfDay, endOfDay, subDays, subMonths, subWeeks } from 'date-fns';
import { arSA, enUS } from 'date-fns/locale';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// ==============================
// === INTERFACES ===
// ==============================
interface Chef {
  _id: string;
  user: {
    _id: string;
    name: string;
    nameEn: string;
    username: string;
    email: string;
  };
  department: Array<{
    _id: string;
    name: string;
    nameEn: string;
    code: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

interface Branch {
  _id: string;
  name: string;
  nameEn: string;
  code: string;
}

interface ProductBranchData {
  assigned: number;
  completed: number;
  completionRate?: number;
}

interface ProductData {
  id: string;
  code: string;
  name: string;
  nameAr: string;
  nameEn?: string;
  unit: string;
  totalAssigned: number;
  totalCompleted: number;
  totalAmount: number;
  completionRate: number;
  branchDistribution: {
    [branchId: string]: ProductBranchData;
  };
}

interface DepartmentData {
  id: string;
  code: string;
  name: string;
  nameAr: string;
  nameEn?: string;
  products: ProductData[];
  productsCount: number;
  totalAssigned: number;
  totalCompleted: number;
  totalAmount: number;
  completionRate: number;
  orderCount: number;
  branches: Array<{
    id: string;
    name: string;
    nameEn: string;
    code: string;
    assigned: number;
    completed: number;
    amount: number;
    orderCount: number;
    completionRate: number;
  }>;
}

interface ReportTotals {
  totalDepartments: number;
  totalProducts: number;
  totalBranches: number;
  totalAssigned: number;
  totalCompleted: number;
  avgCompletionRate: number;
}

// ==============================
// === CONSTANTS ===
// ==============================
const PERIOD_OPTIONS = [
  { value: 'today', labelAr: 'اليوم', labelEn: 'Today' },
  { value: 'yesterday', labelAr: 'أمس', labelEn: 'Yesterday' },
  { value: 'week', labelAr: 'آخر 7 أيام', labelEn: 'Last 7 Days' },
  { value: 'month', labelAr: 'آخر 30 يوم', labelEn: 'Last 30 Days' },
  { value: 'quarter', labelAr: 'آخر 3 شهور', labelEn: 'Last 3 Months' },
  { value: 'custom', labelAr: 'فترة مخصصة', labelEn: 'Custom Range' },
];

const SORT_OPTIONS = [
  { value: 'name_asc', labelAr: 'الاسم (أ-ي)', labelEn: 'Name (A-Z)' },
  { value: 'name_desc', labelAr: 'الاسم (ي-أ)', labelEn: 'Name (Z-A)' },
  { value: 'assigned_desc', labelAr: 'المطلوب (الأكثر أولاً)', labelEn: 'Assigned (High to Low)' },
  { value: 'completed_desc', labelAr: 'المكتمل (الأكثر أولاً)', labelEn: 'Completed (High to Low)' },
  { value: 'rate_desc', labelAr: 'نسبة الإنجاز (الأعلى أولاً)', labelEn: 'Completion Rate (High to Low)' },
];

// ==============================
// === UTILITY FUNCTIONS ===
// ==============================
const formatNumberWithComma = (num: number): string => {
  if (isNaN(num) || num === null || num === undefined) return '0';
  
  if (num % 1 !== 0) {
    const rounded = Math.round(num * 100) / 100;
    const parts = rounded.toString().split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const decimalPart = parts[1] ? parts[1].padEnd(2, '0').substring(0, 2) : '00';
    return `${integerPart}.${decimalPart}`;
  }
  
  return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const toArabicNumerals = (number: string | number): string => {
  if (number === null || number === undefined) return '0';
  const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  
  const numberStr = String(number);
  const parts = numberStr.split('.');
  
  const integerPart = parts[0].replace(/[0-9]/g, (digit) => arabicNumerals[parseInt(digit)]);
  
  if (parts[1]) {
    const decimalPart = parts[1].replace(/[0-9]/g, (digit) => arabicNumerals[parseInt(digit)]);
    return `${integerPart}.${decimalPart}`;
  }
  
  return integerPart;
};

const formatNumber = (num: number, isRtl: boolean): string => {
  const formatted = formatNumberWithComma(num);
  return isRtl ? toArabicNumerals(formatted) : formatted;
};

const formatPercentage = (num: number, isRtl: boolean): string => {
  const formatted = Math.round(num * 100) / 100;
  return `${formatNumber(formatted, isRtl)}%`;
};

const fixArabicText = (text: string): string => {
  if (!text || typeof text !== 'string') return text || '';
  
  let fixedText = text.trim();
  fixedText = fixedText
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  
  return fixedText;
};

const getRandomColor = (index: number): string => {
  const colors = [
    'bg-blue-500', 'bg-emerald-500', 'bg-purple-500', 'bg-pink-500', 
    'bg-indigo-500', 'bg-teal-500', 'bg-amber-500', 'bg-cyan-500',
    'bg-red-500', 'bg-green-500'
  ];
  return colors[index % colors.length];
};

const getCompletionRateColor = (rate: number): string => {
  if (rate >= 90) return 'text-emerald-700 bg-emerald-50 border-emerald-200';
  if (rate >= 70) return 'text-green-700 bg-green-50 border-green-200';
  if (rate >= 50) return 'text-amber-700 bg-amber-50 border-amber-200';
  return 'text-red-700 bg-red-50 border-red-200';
};

const calculatePeriodDates = (period: string) => {
  const now = new Date();
  let start = new Date();
  let end = new Date();

  switch (period) {
    case 'today':
      start = startOfDay(now);
      end = endOfDay(now);
      break;
    case 'yesterday':
      start = startOfDay(subDays(now, 1));
      end = endOfDay(subDays(now, 1));
      break;
    case 'week':
      start = startOfDay(subWeeks(now, 1));
      end = endOfDay(now);
      break;
    case 'month':
      start = startOfDay(subMonths(now, 1));
      end = endOfDay(now);
      break;
    case 'quarter':
      start = startOfDay(subMonths(now, 3));
      end = endOfDay(now);
      break;
    default:
      start = startOfDay(subMonths(now, 1));
      end = endOfDay(now);
  }

  return { start, end };
};

// ==============================
// === PDF FONT LOADER ===
// ==============================
const loadAmiriFont = async (doc: jsPDF): Promise<boolean> => {
  const fontName = 'Amiri';
  try {
    const [regularRes, boldRes] = await Promise.all([
      fetch('https://cdn.jsdelivr.net/gh/aliftype/amiri@main/fonts/Amiri-Regular.ttf'),
      fetch('https://cdn.jsdelivr.net/gh/aliftype/amiri@main/fonts/Amiri-Bold.ttf'),
    ]);
    const [regularFontBytes, boldFontBytes] = await Promise.all([
      regularRes.arrayBuffer(),
      boldRes.arrayBuffer(),
    ]);
    const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
      let binary = '';
      const bytes = new Uint8Array(buffer);
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      return window.btoa(binary);
    };
    doc.addFileToVFS(`${fontName}-normal.ttf`, arrayBufferToBase64(regularFontBytes));
    doc.addFont(`${fontName}-normal.ttf`, fontName, 'normal');
    doc.addFileToVFS(`${fontName}-bold.ttf`, arrayBufferToBase64(boldFontBytes));
    doc.addFont(`${fontName}-bold.ttf`, fontName, 'bold');
    return true;
  } catch (error) {
    console.warn('Failed to load Amiri font. Using fallback.');
    return false;
  }
};

// ==============================
// === ENHANCED EXPORT FUNCTIONS ===
// ==============================

// تصدير قسم واحد إلى PDF
const exportSingleDepartmentToPDF = async (
  department: DepartmentData,
  branches: Branch[],
  periodLabel: string,
  showCompleted: boolean,
  isRtl: boolean,
  language: string
) => {
  const toastId = 'pdf-export';
  try {
    toast.info(isRtl ? 'جارٍ إنشاء ملف PDF...' : 'Generating PDF...', {
      position: isRtl ? 'top-left' : 'top-right',
      autoClose: false,
      toastId,
    });

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    const fontLoaded = await loadAmiriFont(doc);
    const fontName = fontLoaded ? 'Amiri' : 'helvetica';
    const pageWidth = doc.internal.pageSize.width;
    const margin = 10;
    
    // تحسين الأحجام والألوان
    const headerBgColor = [245, 158, 11]; // amber-500
    const totalRowColor = [245, 158, 11]; // amber-500
    const altRowColor = [249, 250, 251]; // gray-50
    const borderColor = [209, 213, 219]; // gray-300
    const textColor = [31, 41, 55]; // gray-800
    const lightTextColor = [107, 114, 128]; // gray-500

    // العنوان الرئيسي بخط أصغر
    doc.setFont(fontName, 'bold');
    doc.setFontSize(14); // تصغير من 18 إلى 14
    doc.setTextColor(...textColor);
    const title = isRtl ? `تقرير قسم ${fixArabicText(department.name)}` : `Department Report: ${department.nameEn || department.name}`;
    doc.text(title, pageWidth / 2, 12, { align: 'center' });

    // معلومات التقرير بخط أصغر
    doc.setFont(fontName, 'normal');
    doc.setFontSize(8); // تصغير من 9 إلى 8
    doc.setTextColor(...lightTextColor);
    
    const infoParts = [];
    infoParts.push(periodLabel);
    infoParts.push(`${department.productsCount} ${isRtl ? 'منتج' : 'products'}`);
    infoParts.push(`${department.branches?.length || 0} ${isRtl ? 'فرع' : 'branches'}`);
    
    const infoText = infoParts.join('  |  ');
    doc.text(infoText, pageWidth / 2, 20, { align: 'center' });

    // معلومات القسم بخط صغير جداً
    doc.setFont(fontName, 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...textColor);
    doc.text(isRtl ? 'معلومات القسم:' : 'Department Info:', margin, 30);
    
    doc.setFont(fontName, 'normal');
    doc.setFontSize(8);
    doc.text(isRtl ? `الكود: ${department.code}` : `Code: ${department.code}`, margin, 35);
    doc.text(isRtl ? `إجمالي المطلوب: ${formatNumberWithComma(department.totalAssigned)}` : `Total Assigned: ${formatNumberWithComma(department.totalAssigned)}`, margin, 40);
    doc.text(isRtl ? `إجمالي المكتمل: ${formatNumberWithComma(department.totalCompleted)}` : `Total Completed: ${formatNumberWithComma(department.totalCompleted)}`, margin, 45);
    doc.text(isRtl ? `نسبة الإنجاز: ${Math.round(department.completionRate)}%` : `Completion Rate: ${Math.round(department.completionRate)}%`, margin, 50);

    let yPos = 55;

    // الحصول على الفروع النشطة لهذا القسم
    const activeBranches = branches.filter(branch => 
      department.products.some(product => 
        product.branchDistribution && product.branchDistribution[branch._id]
      )
    );

    // إنشاء الجدول - الترتيب من اليمين لليسار للعربية
    let headers: string[];
    let columnStyles: any = {};
    
    if (isRtl) {
      // للعربية: ترتيب معكوس من اليمين لليسار
      headers = [
        'نسبة الإنجاز',          // أول عمود من اليمين
        'إجمالي المكتمل',        // ثاني عمود
        'إجمالي المطلوب',        // ثالث عمود
        ...activeBranches.map(branch => 
          language === 'ar' ? fixArabicText(branch.name) : branch.nameEn
        ).reverse(),             // جميع الفروع معكوسة
        'اسم المنتج',            // قبل الأخير
        'الكود'                  // آخر عمود من اليسار
      ];
      
      // تحديد أنماط الأعمدة للعربية
      const branchCount = activeBranches.length;
      const totalColumns = 5 + branchCount; // 5 أعمدة ثابتة + الفروع
      
      // أعمدة من اليمين لليسار
      columnStyles[0] = { // نسبة الإنجاز (يمين)
        cellWidth: 20,
        fontStyle: 'bold',
        halign: 'center',
        fillColor: [219, 234, 254], // blue-100
        valign: 'middle',
        fontSize: 7
      };
      
      columnStyles[1] = { // إجمالي المكتمل
        cellWidth: 20,
        fontStyle: 'bold',
        halign: 'center',
        fillColor: [209, 250, 229], // emerald-100
        valign: 'middle',
        fontSize: 7
      };
      
      columnStyles[2] = { // إجمالي المطلوب
        cellWidth: 20,
        fontStyle: 'bold',
        halign: 'center',
        fillColor: [254, 243, 199], // amber-100
        valign: 'middle',
        fontSize: 7
      };
      
      // أعمدة الفروع (الوسط)
      const branchWidth = Math.max(12, Math.floor((pageWidth - margin * 2 - 100) / branchCount));
      for (let i = 3; i < 3 + branchCount; i++) {
        columnStyles[i] = { 
          cellWidth: branchWidth,
          halign: 'center',
          valign: 'middle',
          fontSize: 7,
          minCellHeight: 6
        };
      }
      
      // عمود اسم المنتج
      columnStyles[3 + branchCount] = { 
        cellWidth: 35, 
        halign: 'center', 
        fontStyle: 'bold',
        valign: 'middle',
        fontSize: 7
      };
      
      // عمود الكود
      columnStyles[4 + branchCount] = { 
        cellWidth: 25, 
        halign: 'center', 
        fontStyle: 'bold',
        valign: 'middle',
        fontSize: 7
      };
    } else {
      // للإنجليزية: ترتيب عادي من اليسار لليمين
      headers = [
        'Code',
        'Product Name',
        ...activeBranches.map(branch => branch.nameEn || branch.name),
        'Total Assigned',
        'Total Completed',
        'Completion Rate'
      ];
      
      const branchCount = activeBranches.length;
      const totalColumns = 5 + branchCount;
      
      // أعمدة من اليسار لليمين
      columnStyles[0] = { // الكود
        cellWidth: 25,
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle',
        fontSize: 7
      };
      
      columnStyles[1] = { // اسم المنتج
        cellWidth: 35,
        halign: 'center',
        fontStyle: 'bold',
        valign: 'middle',
        fontSize: 7
      };
      
      // أعمدة الفروع
      const branchWidth = Math.max(12, Math.floor((pageWidth - margin * 2 - 100) / branchCount));
      for (let i = 2; i < 2 + branchCount; i++) {
        columnStyles[i] = { 
          cellWidth: branchWidth,
          halign: 'center',
          valign: 'middle',
          fontSize: 7,
          minCellHeight: 6
        };
      }
      
      columnStyles[2 + branchCount] = { // إجمالي المطلوب
        cellWidth: 20,
        fontStyle: 'bold',
        halign: 'center',
        fillColor: [254, 243, 199], // amber-100
        valign: 'middle',
        fontSize: 7
      };
      
      columnStyles[3 + branchCount] = { // إجمالي المكتمل
        cellWidth: 20,
        fontStyle: 'bold',
        halign: 'center',
        fillColor: [209, 250, 229], // emerald-100
        valign: 'middle',
        fontSize: 7
      };
      
      columnStyles[4 + branchCount] = { // نسبة الإنجاز
        cellWidth: 20,
        fontStyle: 'bold',
        halign: 'center',
        fillColor: [219, 234, 254], // blue-100
        valign: 'middle',
        fontSize: 7
      };
    }

    // تحضير بيانات الجدول
    const body = department.products.map((product, index) => {
      if (isRtl) {
        // للعربية: ترتيب معكوس
        const branchValues = activeBranches.map(branch => {
          const branchData = product.branchDistribution?.[branch._id];
          return branchData?.assigned || 0;
        }).reverse();
        
        return [
          `${Math.round(product.completionRate || 0)}%`, // نسبة الإنجاز
          formatNumberWithComma(product.totalCompleted), // إجمالي المكتمل
          formatNumberWithComma(product.totalAssigned),  // إجمالي المطلوب
          ...branchValues,                              // جميع الفروع
          fixArabicText(product.name),                  // اسم المنتج
          product.code                                  // الكود
        ];
      } else {
        // للإنجليزية: ترتيب عادي
        return [
          product.code, // الكود
          language === 'en' && product.nameEn ? product.nameEn : fixArabicText(product.name), // اسم المنتج
          ...activeBranches.map(branch => {
            const branchData = product.branchDistribution?.[branch._id];
            return branchData?.assigned || 0;
          }), // الفروع
          formatNumberWithComma(product.totalAssigned), // إجمالي المطلوب
          formatNumberWithComma(product.totalCompleted), // إجمالي المكتمل
          `${Math.round(product.completionRate || 0)}%`  // نسبة الإنجاز
        ];
      }
    });
    
    // إضافة صف الإجماليات
    if (isRtl) {
      // حساب إجمالي كل فرع
      const totalBranchValues = activeBranches.map(branch => 
        formatNumberWithComma(
          department.products.reduce((sum, product) => 
            sum + (product.branchDistribution?.[branch._id]?.assigned || 0), 0
          )
        )
      ).reverse();
      
      body.push([
        `${Math.round(department.completionRate)}%`, // نسبة الإنجاز
        formatNumberWithComma(department.totalCompleted), // إجمالي المكتمل
        formatNumberWithComma(department.totalAssigned),  // إجمالي المطلوب
        ...totalBranchValues,                           // إجمالي الفروع
        'الإجمالي',                                     // اسم المنتج
        ''                                              // الكود
      ]);
    } else {
      body.push([
        '', // الكود
        'Total', // اسم المنتج
        ...activeBranches.map(branch => 
          formatNumberWithComma(
            department.products.reduce((sum, product) => 
              sum + (product.branchDistribution?.[branch._id]?.assigned || 0), 0
            )
          )
        ), // إجمالي الفروع
        formatNumberWithComma(department.totalAssigned), // إجمالي المطلوب
        formatNumberWithComma(department.totalCompleted), // إجمالي المكتمل
        `${Math.round(department.completionRate)}%`      // نسبة الإنجاز
      ]);
    }

    // إنشاء الجدول
    autoTable(doc, {
      head: [headers],
      body: body,
      startY: yPos,
      margin: { left: margin, right: margin },
      theme: 'grid',
      styles: {
        font: fontName,
        fontSize: 7, // تصغير خط الجدول
        textColor: textColor,
        lineColor: borderColor,
        lineWidth: 0.1,
        halign: 'center',
        valign: 'middle',
        cellPadding: 2, // تقليل الحشو
        minCellHeight: 6, // تقليل الارتفاع
        overflow: 'ellipsize'
      },
      headStyles: {
        fillColor: headerBgColor,
        textColor: 255,
        fontSize: 8, // تصغير خط الرأس
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle',
        cellPadding: 2,
        lineWidth: 0.1,
        lineColor: [217, 119, 6],
        minCellHeight: 7
      },
      bodyStyles: {
        fontSize: 7,
        halign: 'center',
        valign: 'middle',
        cellPadding: 1,
        lineWidth: 0.1,
        minCellHeight: 6,
        overflow: 'ellipsize'
      },
      alternateRowStyles: { 
        fillColor: altRowColor 
      },
      columnStyles: columnStyles,
      didParseCell: (data: any) => {
        // صف الإجماليات
        if (data.section === 'body' && data.row.index === body.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = totalRowColor;
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontSize = 8;
          data.cell.styles.minCellHeight = 7;
        }
        
        // تحويل الأرقام إلى عربية للغة العربية
        if (isRtl && data.section === 'body' && typeof data.cell.text === 'string') {
          // تحويل الأرقام فقط (مع الفواصل والنسب المئوية)
          data.cell.text = data.cell.text
            .replace(/\d+/g, (match: string) => toArabicNumerals(match))
            .replace(/%/g, '٪');
        }
        
        // معالجة رؤوس الأعمدة لمنع التفاف النص
        if (data.section === 'head') {
          data.cell.styles.overflow = 'ellipsize';
          data.cell.styles.lineHeight = 1.1;
        }
        
        // تلوين خلايا الفروع حسب القيمة
        let branchStartIndex, branchEndIndex;
        if (isRtl) {
          branchStartIndex = 3; // أول فرع بعد الأعمدة الثلاثة الأولى
          branchEndIndex = 3 + activeBranches.length; // نهاية الفروع
        } else {
          branchStartIndex = 2; // أول فرع بعد الكود واسم المنتج
          branchEndIndex = 2 + activeBranches.length; // نهاية الفروع قبل الأعمدة الثلاثة الأخيرة
        }
        
        if (data.section === 'body' && 
            data.column.index >= branchStartIndex && 
            data.column.index < branchEndIndex &&
            data.row.index < body.length - 1) { // ليس صف الإجماليات
          const value = data.cell.raw;
          let numValue = value;
          if (typeof value === 'string' && value.includes(',')) {
            numValue = parseInt(value.replace(/,/g, '')) || 0;
          }
          
          if (typeof numValue === 'number' && numValue > 0) {
            if (numValue <= 10) {
              data.cell.styles.fillColor = [255, 251, 235]; // amber-50
              data.cell.styles.textColor = [146, 64, 14]; // amber-800
            } else if (numValue <= 50) {
              data.cell.styles.fillColor = [236, 253, 245]; // emerald-50
              data.cell.styles.textColor = [6, 95, 70]; // emerald-800
            } else if (numValue <= 100) {
              data.cell.styles.fillColor = [209, 250, 229]; // emerald-100
              data.cell.styles.textColor = [4, 120, 87]; // emerald-700
            } else {
              data.cell.styles.fillColor = [167, 243, 208]; // emerald-200
              data.cell.styles.textColor = [6, 95, 70]; // emerald-800
            }
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });

    // إضافة ملاحظة بخط صغير جداً
    const finalY = (doc as any).lastAutoTable.finalY || yPos + 100;
    doc.setFontSize(6); // تصغير خط الملاحظة
    doc.setTextColor(...lightTextColor);
    const note = isRtl 
      ? '* تم إنشاء هذا التقرير بواسطة نظام إدارة الجودياء'
      : '* This report was generated by aljodia Management System';
    doc.text(note, pageWidth / 2, finalY + 8, { align: 'center' });

    // اسم الملف
    const dateStr = new Date().toISOString().split('T')[0];
    const fileName = isRtl 
      ? `تقرير_${department.code}_${dateStr}.pdf`
      : `report_${department.code}_${dateStr}.pdf`;

    // حفظ الملف
    doc.save(fileName);

    toast.update(toastId, {
      render: isRtl ? 'تم التصدير إلى PDF بنجاح!' : 'Exported to PDF successfully!',
      type: 'success',
      autoClose: 3000,
    });
  } catch (error) {
    console.error('PDF Export Error:', error);
    toast.update(toastId, {
      render: isRtl ? 'فشل تصدير PDF. حاول مرة أخرى.' : 'PDF export failed. Please try again.',
      type: 'error',
      autoClose: 4000,
    });
  }
};

// تصدير جميع الأقسام إلى PDF
const exportAllDepartmentsToPDF = async (
  departmentsData: DepartmentData[],
  branches: Branch[],
  periodLabel: string,
  showCompleted: boolean,
  isRtl: boolean,
  language: string
) => {
  const toastId = 'pdf-export';
  try {
    toast.info(isRtl ? 'جارٍ إنشاء ملف PDF...' : 'Generating PDF...', {
      position: isRtl ? 'top-left' : 'top-right',
      autoClose: false,
      toastId,
    });

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    const fontLoaded = await loadAmiriFont(doc);
    const fontName = fontLoaded ? 'Amiri' : 'helvetica';
    const pageWidth = doc.internal.pageSize.width;
    const margin = 10;
    
    // تحسين الأحجام والألوان
    const headerBgColor = [245, 158, 11]; // amber-500
    const totalRowColor = [245, 158, 11]; // amber-500
    const altRowColor = [249, 250, 251]; // gray-50
    const borderColor = [209, 213, 219]; // gray-300
    const textColor = [31, 41, 55]; // gray-800
    const lightTextColor = [107, 114, 128]; // gray-500

    // العنوان الرئيسي بخط أصغر
    doc.setFont(fontName, 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...textColor);
    const title = isRtl ? 'تقرير ملخص الأقسام' : 'Departments Summary Report';
    doc.text(title, pageWidth / 2, 12, { align: 'center' });

    // معلومات التقرير بخط أصغر
    doc.setFont(fontName, 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...lightTextColor);
    
    const infoParts = [];
    infoParts.push(periodLabel);
    infoParts.push(`${departmentsData.length} ${isRtl ? 'قسم' : 'departments'}`);
    const totalProducts = departmentsData.reduce((sum, dept) => sum + dept.productsCount, 0);
    infoParts.push(`${totalProducts} ${isRtl ? 'منتج' : 'products'}`);
    
    const infoText = infoParts.join('  |  ');
    doc.text(infoText, pageWidth / 2, 20, { align: 'center' });

    let yPos = 30;

    // إنشاء الجدول - الترتيب من اليمين لليسار للعربية
    let headers: string[];
    let columnStyles: any = {};
    
    if (isRtl) {
      // للعربية: ترتيب معكوس من اليمين لليسار
      headers = [
        'عدد الفروع',          // أول عمود من اليمين
        'نسبة الإنجاز',        // ثاني عمود
        'إجمالي المكتمل',      // ثالث عمود
        'إجمالي المطلوب',      // رابع عمود
        'عدد المنتجات',        // خامس عمود
        'اسم القسم',           // سادس عمود
        'كود القسم'            // آخر عمود من اليسار
      ];
      
      // تحديد أنماط الأعمدة للعربية
      columnStyles[0] = { // عدد الفروع (يمين)
        cellWidth: 20,
        fontStyle: 'bold',
        halign: 'center',
        fillColor: [219, 234, 254], // blue-100
        valign: 'middle',
        fontSize: 7
      };
      
      columnStyles[1] = { // نسبة الإنجاز
        cellWidth: 20,
        fontStyle: 'bold',
        halign: 'center',
        fillColor: [209, 250, 229], // emerald-100
        valign: 'middle',
        fontSize: 7
      };
      
      columnStyles[2] = { // إجمالي المكتمل
        cellWidth: 25,
        fontStyle: 'bold',
        halign: 'center',
        fillColor: [209, 250, 229], // emerald-100
        valign: 'middle',
        fontSize: 7
      };
      
      columnStyles[3] = { // إجمالي المطلوب
        cellWidth: 25,
        fontStyle: 'bold',
        halign: 'center',
        fillColor: [254, 243, 199], // amber-100
        valign: 'middle',
        fontSize: 7
      };
      
      columnStyles[4] = { // عدد المنتجات
        cellWidth: 20,
        halign: 'center',
        valign: 'middle',
        fontSize: 7
      };
      
      columnStyles[5] = { // اسم القسم
        cellWidth: 40,
        halign: 'center',
        fontStyle: 'bold',
        valign: 'middle',
        fontSize: 7
      };
      
      columnStyles[6] = { // كود القسم
        cellWidth: 25,
        halign: 'center',
        fontStyle: 'bold',
        valign: 'middle',
        fontSize: 7
      };
    } else {
      // للإنجليزية: ترتيب عادي
      headers = [
        'Dept Code',
        'Department Name',
        'Products Count',
        'Total Assigned',
        'Total Completed',
        'Completion Rate',
        'Branches Count'
      ];
      
      columnStyles[0] = { // كود القسم
        cellWidth: 25,
        halign: 'center',
        fontStyle: 'bold',
        valign: 'middle',
        fontSize: 7
      };
      
      columnStyles[1] = { // اسم القسم
        cellWidth: 40,
        halign: 'center',
        fontStyle: 'bold',
        valign: 'middle',
        fontSize: 7
      };
      
      columnStyles[2] = { // عدد المنتجات
        cellWidth: 20,
        halign: 'center',
        valign: 'middle',
        fontSize: 7
      };
      
      columnStyles[3] = { // إجمالي المطلوب
        cellWidth: 25,
        fontStyle: 'bold',
        halign: 'center',
        fillColor: [254, 243, 199], // amber-100
        valign: 'middle',
        fontSize: 7
      };
      
      columnStyles[4] = { // إجمالي المكتمل
        cellWidth: 25,
        fontStyle: 'bold',
        halign: 'center',
        fillColor: [209, 250, 229], // emerald-100
        valign: 'middle',
        fontSize: 7
      };
      
      columnStyles[5] = { // نسبة الإنجاز
        cellWidth: 20,
        fontStyle: 'bold',
        halign: 'center',
        fillColor: [219, 234, 254], // blue-100
        valign: 'middle',
        fontSize: 7
      };
      
      columnStyles[6] = { // عدد الفروع
        cellWidth: 20,
        fontStyle: 'bold',
        halign: 'center',
        fillColor: [219, 234, 254], // blue-100
        valign: 'middle',
        fontSize: 7
      };
    }

    // تحضير بيانات الجدول
    const body = departmentsData.map((dept, index) => {
      const activeBranches = dept.branches?.filter(b => b.assigned > 0).length || 0;
      
      if (isRtl) {
        // للعربية: ترتيب معكوس
        return [
          activeBranches.toString(),               // عدد الفروع
          `${Math.round(dept.completionRate)}%`,   // نسبة الإنجاز
          formatNumberWithComma(dept.totalCompleted), // إجمالي المكتمل
          formatNumberWithComma(dept.totalAssigned),  // إجمالي المطلوب
          dept.productsCount.toString(),           // عدد المنتجات
          fixArabicText(dept.name),                // اسم القسم
          dept.code                                // كود القسم
        ];
      } else {
        // للإنجليزية: ترتيب عادي
        return [
          dept.code, // كود القسم
          language === 'en' && dept.nameEn ? dept.nameEn : fixArabicText(dept.name), // اسم القسم
          dept.productsCount, // عدد المنتجات
          formatNumberWithComma(dept.totalAssigned), // إجمالي المطلوب
          formatNumberWithComma(dept.totalCompleted), // إجمالي المكتمل
          `${Math.round(dept.completionRate)}%`, // نسبة الإنجاز
          activeBranches // عدد الفروع
        ];
      }
    });
    
    // إضافة صف الإجماليات
    const totalAssigned = departmentsData.reduce((sum, dept) => sum + dept.totalAssigned, 0);
    const totalCompleted = departmentsData.reduce((sum, dept) => sum + dept.totalCompleted, 0);
    const avgCompletionRate = departmentsData.length > 0 
      ? departmentsData.reduce((sum, dept) => sum + dept.completionRate, 0) / departmentsData.length 
      : 0;
    
    if (isRtl) {
      body.push([
        '', // عدد الفروع
        `${Math.round(avgCompletionRate)}%`, // نسبة الإنجاز
        formatNumberWithComma(totalCompleted), // إجمالي المكتمل
        formatNumberWithComma(totalAssigned),  // إجمالي المطلوب
        totalProducts.toString(),              // عدد المنتجات
        'الإجمالي',                            // اسم القسم
        ''                                     // كود القسم
      ]);
    } else {
      body.push([
        '', // كود القسم
        'Total', // اسم القسم
        totalProducts, // عدد المنتجات
        formatNumberWithComma(totalAssigned), // إجمالي المطلوب
        formatNumberWithComma(totalCompleted), // إجمالي المكتمل
        `${Math.round(avgCompletionRate)}%`, // نسبة الإنجاز
        '' // عدد الفروع
      ]);
    }

    // إنشاء الجدول
    autoTable(doc, {
      head: [headers],
      body: body,
      startY: yPos,
      margin: { left: margin, right: margin },
      theme: 'grid',
      styles: {
        font: fontName,
        fontSize: 7,
        textColor: textColor,
        lineColor: borderColor,
        lineWidth: 0.1,
        halign: 'center',
        valign: 'middle',
        cellPadding: 2,
        minCellHeight: 6,
        overflow: 'ellipsize'
      },
      headStyles: {
        fillColor: headerBgColor,
        textColor: 255,
        fontSize: 8,
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle',
        cellPadding: 2,
        lineWidth: 0.1,
        lineColor: [217, 119, 6],
        minCellHeight: 7
      },
      bodyStyles: {
        fontSize: 7,
        halign: 'center',
        valign: 'middle',
        cellPadding: 1,
        lineWidth: 0.1,
        minCellHeight: 6,
        overflow: 'ellipsize'
      },
      alternateRowStyles: { 
        fillColor: altRowColor 
      },
      columnStyles: columnStyles,
      didParseCell: (data: any) => {
        // صف الإجماليات
        if (data.section === 'body' && data.row.index === body.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = totalRowColor;
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontSize = 8;
          data.cell.styles.minCellHeight = 7;
        }
        
        // تحويل الأرقام إلى عربية للغة العربية
        if (isRtl && data.section === 'body' && typeof data.cell.text === 'string') {
          data.cell.text = data.cell.text
            .replace(/\d+/g, (match: string) => toArabicNumerals(match))
            .replace(/%/g, '٪');
        }
        
        // تلوين خلايا النسب المئوية
        if ((isRtl && data.column.index === 1) || (!isRtl && data.column.index === 5)) {
          if (data.section === 'body' && data.row.index < body.length - 1) {
            const rateText = data.cell.text?.toString() || '';
            const rate = parseInt(rateText.replace(/[^0-9]/g, '')) || 0;
            
            if (rate >= 90) {
              data.cell.styles.fillColor = [209, 250, 229];
              data.cell.styles.textColor = [6, 95, 70];
            } else if (rate >= 70) {
              data.cell.styles.fillColor = [220, 252, 231];
              data.cell.styles.textColor = [22, 163, 74];
            } else if (rate >= 50) {
              data.cell.styles.fillColor = [254, 243, 199];
              data.cell.styles.textColor = [146, 64, 14];
            } else {
              data.cell.styles.fillColor = [254, 226, 226];
              data.cell.styles.textColor = [153, 27, 27];
            }
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });

    // إضافة ملاحظة بخط صغير جداً
    const finalY = (doc as any).lastAutoTable.finalY || yPos + 100;
    doc.setFontSize(6);
    doc.setTextColor(...lightTextColor);
    const note = isRtl 
      ? '* تم إنشاء هذا التقرير بواسطة نظام إدارة الجودياء'
      : '* This report was generated by aljodia Management System';
    doc.text(note, pageWidth / 2, finalY + 8, { align: 'center' });

    // اسم الملف
    const dateStr = new Date().toISOString().split('T')[0];
    const fileName = isRtl 
      ? `تقرير_ملخص_الأقسام_${dateStr}.pdf`
      : `departments_summary_report_${dateStr}.pdf`;

    // حفظ الملف
    doc.save(fileName);

    toast.update(toastId, {
      render: isRtl ? 'تم التصدير إلى PDF بنجاح!' : 'Exported to PDF successfully!',
      type: 'success',
      autoClose: 3000,
    });
  } catch (error) {
    console.error('PDF Export Error:', error);
    toast.update(toastId, {
      render: isRtl ? 'فشل تصدير PDF. حاول مرة أخرى.' : 'PDF export failed. Please try again.',
      type: 'error',
      autoClose: 4000,
    });
  }
};

// تصدير قسم واحد إلى Excel
const exportSingleDepartmentToExcel = async (
  department: DepartmentData,
  branches: Branch[],
  periodLabel: string,
  showCompleted: boolean,
  isRtl: boolean,
  language: string
) => {
  const toastId = 'excel-export';
  try {
    toast.info(isRtl ? 'جارٍ إنشاء ملف Excel...' : 'Generating Excel...', {
      position: isRtl ? 'top-left' : 'top-right',
      autoClose: false,
      toastId,
    });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(
      isRtl ? fixArabicText(department.name).substring(0, 31) : 
      (department.nameEn || department.name).substring(0, 31),
      { views: [{ rightToLeft: isRtl }] }
    );

    // العنوان الرئيسي
    const titleRow = worksheet.addRow([
      isRtl ? `تقرير قسم ${fixArabicText(department.name)}` : 
      `Department Report: ${department.nameEn || department.name}`
    ]);
    titleRow.height = 22;
    const titleCell = titleRow.getCell(1);
    titleCell.font = { size: 13, bold: true, color: { argb: '374151' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FEF3C7' } // amber-100
    };

    // معلومات التقرير
    const activeBranches = branches.filter(branch => 
      department.products.some(product => 
        product.branchDistribution && product.branchDistribution[branch._id]
      )
    );

    const infoParts = [
      periodLabel,
      isRtl ? `${department.productsCount} منتج` : `${department.productsCount} products`,
      isRtl ? `${activeBranches.length} فرع` : `${activeBranches.length} branches`,
      isRtl ? `الكود: ${department.code}` : `Code: ${department.code}`
    ];
    
    const infoRow = worksheet.addRow([infoParts.join('  |  ')]);
    const infoCell = infoRow.getCell(1);
    infoCell.font = { size: 9, color: { argb: '6B7280' } };
    infoCell.alignment = { horizontal: 'center', vertical: 'middle' };

    worksheet.addRow([]); // سطر فارغ

    // معلومات القسم
    const deptInfoRow = worksheet.addRow([
      isRtl ? 'معلومات القسم' : 'Department Information'
    ]);
    deptInfoRow.height = 20;
    const deptInfoCell = deptInfoRow.getCell(1);
    deptInfoCell.font = { size: 11, bold: true, color: { argb: '374151' } };
    deptInfoCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'E5E7EB' } // gray-200
    };

    const detailsRows = [
      [isRtl ? 'إجمالي المطلوب:' : 'Total Assigned:', department.totalAssigned],
      [isRtl ? 'إجمالي المكتمل:' : 'Total Completed:', department.totalCompleted],
      [isRtl ? 'نسبة الإنجاز:' : 'Completion Rate:', department.completionRate / 100]
    ];
    
    detailsRows.forEach(([label, value]) => {
      const row = worksheet.addRow([label, value]);
      row.height = 18;
      row.getCell(1).font = { bold: true, size: 10 };
      row.getCell(2).font = { size: 10 };
      row.getCell(2).numFmt = typeof value === 'number' ? 
        (value < 1 ? '0.00%' : '#,##0.00') : '';
      row.getCell(2).alignment = { horizontal: 'left' };
    });

    worksheet.addRow([]); // سطر فارغ

    // رؤوس أعمدة الجدول حسب اللغة والاتجاه
    let headers: string[];
    
    if (isRtl) {
      headers = [
        'الكود',
        'اسم المنتج',
        ...activeBranches.map(branch => 
          language === 'ar' ? fixArabicText(branch.name) : branch.nameEn
        ),
        'إجمالي المطلوب',
        'إجمالي المكتمل',
        'نسبة الإنجاز'
      ];
    } else {
      headers = [
        'Code',
        'Product Name',
        ...activeBranches.map(branch => branch.nameEn || branch.name),
        'Total Assigned',
        'Total Completed',
        'Completion Rate'
      ];
    }

    const headerRow = worksheet.addRow(headers);
    headerRow.height = 22;
    
    headerRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true, size: 10, color: { argb: 'FFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'F59E0B' } // amber-500
      };
      cell.alignment = { 
        horizontal: 'center', 
        vertical: 'middle',
        wrapText: true
      };
      cell.border = {
        top: { style: 'thin', color: { argb: 'D97706' } },
        left: { style: 'thin', color: { argb: 'D97706' } },
        bottom: { style: 'thin', color: { argb: 'D97706' } },
        right: { style: 'thin', color: { argb: 'D97706' } }
      };
    });

    // البيانات
    department.products.forEach((product, rowIndex) => {
      const rowData = [
        product.code,
        language === 'en' && product.nameEn ? product.nameEn : fixArabicText(product.name),
        ...activeBranches.map(branch => {
          const branchData = product.branchDistribution?.[branch._id];
          return branchData?.assigned || 0;
        }),
        product.totalAssigned,
        product.totalCompleted,
        product.completionRate / 100
      ];
      
      const row = worksheet.addRow(rowData);
      row.height = 20;
      
      row.eachCell((cell, colNumber) => {
        cell.alignment = { 
          horizontal: 'center', 
          vertical: 'middle',
          wrapText: true
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'E5E7EB' } },
          left: { style: 'thin', color: { argb: 'E5E7EB' } },
          bottom: { style: 'thin', color: { argb: 'E5E7EB' } },
          right: { style: 'thin', color: { argb: 'E5E7EB' } }
        };
        
        // صفوف متناوبة
        if (rowIndex % 2 === 0) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'F9FAFB' } // gray-50
          };
        }
        
        // تنسيق الأرقام
        if (colNumber > 2 && colNumber <= headers.length - 3) { // أعمدة الفروع
          cell.numFmt = '#,##0.00';
          
          const quantity = cell.value as number || 0;
          if (quantity > 0) {
            if (quantity <= 10) {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FEF3C7' } // amber-100
              };
              cell.font = { color: { argb: '92400E' } }; // amber-800
            } else if (quantity <= 50) {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'D1FAE5' } // emerald-100
              };
              cell.font = { color: { argb: '065F46' } }; // emerald-800
            } else if (quantity <= 100) {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'A7F3D0' } // emerald-200
              };
              cell.font = { color: { argb: '047857' }, bold: true }; // emerald-700
            } else {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: '86EFAC' } // emerald-300
              };
              cell.font = { color: { argb: '065F46' }, bold: true }; // emerald-800
            }
          }
        } else if (colNumber === headers.length - 2) { // عمود الإجمالي المطلوب
          cell.numFmt = '#,##0.00';
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FEF3C7' } // amber-100
          };
          cell.font = { color: { argb: '92400E' }, bold: true }; // amber-800
        } else if (colNumber === headers.length - 1) { // عمود الإجمالي المكتمل
          cell.numFmt = '#,##0.00';
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'D1FAE5' } // emerald-100
          };
          cell.font = { color: { argb: '065F46' }, bold: true }; // emerald-800
        } else if (colNumber === headers.length) { // عمود النسبة
          cell.numFmt = '0.00%';
          const rate = cell.value as number || 0;
          if (rate >= 0.9) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D1FAE5' } };
            cell.font = { color: { argb: '065F46' }, bold: true };
          } else if (rate >= 0.7) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DCFCE7' } };
            cell.font = { color: { argb: '16A34A' }, bold: true };
          } else if (rate >= 0.5) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEF3C7' } };
            cell.font = { color: { argb: '92400E' }, bold: true };
          } else {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEE2E2' } };
            cell.font = { color: { argb: '991B1B' }, bold: true };
          }
        }
      });
    });

    // إضافة صف الإجماليات
    const totalBranchValues = activeBranches.map(branch => 
      department.products.reduce((sum, product) => 
        sum + (product.branchDistribution?.[branch._id]?.assigned || 0), 0
      )
    );
    
    const totalRowData = [
      isRtl ? 'الإجمالي' : 'Total',
      '',
      ...totalBranchValues,
      department.totalAssigned,
      department.totalCompleted,
      department.completionRate / 100
    ];
    
    const totalRow = worksheet.addRow(totalRowData);
    totalRow.height = 22;
    
    totalRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true, size: 11, color: { argb: 'FFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'F59E0B' } // amber-500
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin', color: { argb: 'D97706' } },
        left: { style: 'thin', color: { argb: 'D97706' } },
        bottom: { style: 'thin', color: { argb: 'D97706' } },
        right: { style: 'thin', color: { argb: 'D97706' } }
      };
      
      // تنسيق الأرقام
      if (colNumber > 2 && colNumber <= headers.length - 3) {
        cell.numFmt = '#,##0.00';
      } else if (colNumber === headers.length - 2) {
        cell.numFmt = '#,##0.00';
      } else if (colNumber === headers.length - 1) {
        cell.numFmt = '#,##0.00';
      } else if (colNumber === headers.length) {
        cell.numFmt = '0.00%';
      }
    });

    // دمج الخلايا للعناوين
    const branchCount = activeBranches.length;
    const totalColumns = 3 + branchCount + 3; // 3 ثابتة + الفروع + 3
    worksheet.mergeCells(`A1:${String.fromCharCode(65 + totalColumns - 1)}1`);
    worksheet.mergeCells(`A2:${String.fromCharCode(65 + totalColumns - 1)}2`);
    worksheet.mergeCells(`A4:${String.fromCharCode(65 + totalColumns - 1)}4`);

    // ضبط عرض الأعمدة
    worksheet.columns = [
      { width: 12 },  // الكود
      { width: 25 },  // اسم المنتج
      ...Array(branchCount).fill({ width: 14 }), // الفروع
      { width: 16 },  // إجمالي المطلوب
      { width: 16 },  // إجمالي المكتمل
      { width: 14 }   // نسبة الإنجاز
    ];

    // حفظ الملف
    const dateStr = new Date().toISOString().split('T')[0];
    const fileName = isRtl 
      ? `تقرير_${department.code}_${dateStr}.xlsx`
      : `report_${department.code}_${dateStr}.xlsx`;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, fileName);

    toast.update(toastId, {
      render: isRtl ? 'تم التصدير إلى Excel بنجاح!' : 'Exported to Excel successfully!',
      type: 'success',
      autoClose: 3000,
    });
  } catch (error) {
    console.error('Excel Export Error:', error);
    toast.update(toastId, {
      render: isRtl ? 'فشل تصدير Excel. حاول مرة أخرى.' : 'Excel export failed. Please try again.',
      type: 'error',
      autoClose: 4000,
    });
  }
};

// تصدير جميع الأقسام إلى Excel
const exportAllDepartmentsToExcel = async (
  departmentsData: DepartmentData[],
  branches: Branch[],
  periodLabel: string,
  showCompleted: boolean,
  isRtl: boolean,
  language: string
) => {
  const toastId = 'excel-export';
  try {
    toast.info(isRtl ? 'جارٍ إنشاء ملف Excel...' : 'Generating Excel...', {
      position: isRtl ? 'top-left' : 'top-right',
      autoClose: false,
      toastId,
    });

    const workbook = new ExcelJS.Workbook();
    
    // إنشاء شيت الملخص
    const summarySheet = workbook.addWorksheet(
      isRtl ? 'ملخص الأقسام' : 'Departments Summary',
      { views: [{ rightToLeft: isRtl }] }
    );

    // عنوان الملخص
    const titleRow = summarySheet.addRow([
      isRtl ? 'تقرير ملخص الأقسام' : 'Departments Summary Report'
    ]);
    titleRow.height = 22;
    const titleCell = titleRow.getCell(1);
    titleCell.font = { size: 13, bold: true, color: { argb: '374151' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FEF3C7' } // amber-100
    };

    // معلومات التقرير
    const totalProducts = departmentsData.reduce((sum, dept) => sum + dept.productsCount, 0);
    const totalBranches = new Set(
      departmentsData.flatMap(dept => 
        dept.branches?.filter(b => b.assigned > 0).map(b => b.id) || []
      )
    ).size;

    const infoParts = [
      periodLabel,
      isRtl ? `${departmentsData.length} قسم` : `${departmentsData.length} departments`,
      isRtl ? `${totalProducts} منتج` : `${totalProducts} products`,
      isRtl ? `${totalBranches} فرع نشط` : `${totalBranches} active branches`
    ];
    
    const infoRow = summarySheet.addRow([infoParts.join('  |  ')]);
    const infoCell = infoRow.getCell(1);
    infoCell.font = { size: 9, color: { argb: '6B7280' } };
    infoCell.alignment = { horizontal: 'center', vertical: 'middle' };

    summarySheet.addRow([]); // سطر فارغ

    // رؤوس أعمدة الملخص حسب اللغة
    let summaryHeaders: string[];
    
    if (isRtl) {
      summaryHeaders = [
        'كود القسم',
        'اسم القسم',
        'عدد المنتجات',
        'إجمالي المطلوب',
        'إجمالي المكتمل',
        'نسبة الإنجاز',
        'عدد الفروع النشطة'
      ];
    } else {
      summaryHeaders = [
        'Dept Code',
        'Department Name',
        'Products Count',
        'Total Assigned',
        'Total Completed',
        'Completion Rate',
        'Active Branches'
      ];
    }

    const summaryHeaderRow = summarySheet.addRow(summaryHeaders);
    summaryHeaderRow.height = 22;
    
    summaryHeaderRow.eachCell((cell) => {
      cell.font = { bold: true, size: 10, color: { argb: 'FFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'F59E0B' } // amber-500
      };
      cell.alignment = { 
        horizontal: 'center', 
        vertical: 'middle',
        wrapText: true
      };
      cell.border = {
        top: { style: 'thin', color: { argb: 'D97706' } },
        left: { style: 'thin', color: { argb: 'D97706' } },
        bottom: { style: 'thin', color: { argb: 'D97706' } },
        right: { style: 'thin', color: { argb: 'D97706' } }
      };
    });

    // بيانات الملخص
    departmentsData.forEach((dept, rowIndex) => {
      const activeBranches = dept.branches?.filter(b => b.assigned > 0).length || 0;
      
      const rowData = [
        dept.code,
        language === 'en' && dept.nameEn ? dept.nameEn : fixArabicText(dept.name),
        dept.productsCount,
        dept.totalAssigned,
        dept.totalCompleted,
        dept.completionRate / 100,
        activeBranches
      ];
      
      const row = summarySheet.addRow(rowData);
      row.height = 20;
      
      row.eachCell((cell, colNumber) => {
        cell.alignment = { 
          horizontal: 'center', 
          vertical: 'middle',
          wrapText: true
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'E5E7EB' } },
          left: { style: 'thin', color: { argb: 'E5E7EB' } },
          bottom: { style: 'thin', color: { argb: 'E5E7EB' } },
          right: { style: 'thin', color: { argb: 'E5E7EB' } }
        };
        
        // صفوف متناوبة
        if (rowIndex % 2 === 0) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'F9FAFB' } // gray-50
          };
        }
        
        // تنسيق الأرقام
        if (colNumber === 3) { // عدد المنتجات
          cell.numFmt = '#,##0';
        } else if (colNumber === 4) { // إجمالي المطلوب
          cell.numFmt = '#,##0.00';
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FEF3C7' } // amber-100
          };
          cell.font = { color: { argb: '92400E' }, bold: true }; // amber-800
        } else if (colNumber === 5) { // إجمالي المكتمل
          cell.numFmt = '#,##0.00';
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'D1FAE5' } // emerald-100
          };
          cell.font = { color: { argb: '065F46' }, bold: true }; // emerald-800
        } else if (colNumber === 6) { // نسبة الإنجاز
          cell.numFmt = '0.00%';
          const rate = cell.value as number || 0;
          if (rate >= 0.9) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D1FAE5' } };
            cell.font = { color: { argb: '065F46' }, bold: true };
          } else if (rate >= 0.7) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DCFCE7' } };
            cell.font = { color: { argb: '16A34A' }, bold: true };
          } else if (rate >= 0.5) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEF3C7' } };
            cell.font = { color: { argb: '92400E' }, bold: true };
          } else {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEE2E2' } };
            cell.font = { color: { argb: '991B1B' }, bold: true };
          }
        } else if (colNumber === 7) { // عدد الفروع
          cell.numFmt = '#,##0';
        }
      });
    });

    // إضافة صف الإجماليات في الملخص
    const totalAssigned = departmentsData.reduce((sum, dept) => sum + dept.totalAssigned, 0);
    const totalCompleted = departmentsData.reduce((sum, dept) => sum + dept.totalCompleted, 0);
    const avgCompletionRate = departmentsData.length > 0 
      ? departmentsData.reduce((sum, dept) => sum + dept.completionRate, 0) / departmentsData.length / 100 
      : 0;

    const totalRowData = [
      isRtl ? 'الإجمالي' : 'Total',
      '',
      totalProducts,
      totalAssigned,
      totalCompleted,
      avgCompletionRate,
      totalBranches
    ];
    
    const totalRow = summarySheet.addRow(totalRowData);
    totalRow.height = 22;
    
    totalRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true, size: 11, color: { argb: 'FFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'F59E0B' } // amber-500
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin', color: { argb: 'D97706' } },
        left: { style: 'thin', color: { argb: 'D97706' } },
        bottom: { style: 'thin', color: { argb: 'D97706' } },
        right: { style: 'thin', color: { argb: 'D97706' } }
      };
      
      // تنسيق الأرقام
      if (colNumber === 3) {
        cell.numFmt = '#,##0';
      } else if (colNumber === 4) {
        cell.numFmt = '#,##0.00';
      } else if (colNumber === 5) {
        cell.numFmt = '#,##0.00';
      } else if (colNumber === 6) {
        cell.numFmt = '0.00%';
      } else if (colNumber === 7) {
        cell.numFmt = '#,##0';
      }
    });

    // دمج الخلايا للعناوين
    summarySheet.mergeCells(`A1:G1`);
    summarySheet.mergeCells(`A2:G2`);

    // ضبط عرض الأعمدة في الملخص
    summarySheet.columns = [
      { width: 12 },  // كود القسم
      { width: 25 },  // اسم القسم
      { width: 12 },  // عدد المنتجات
      { width: 16 },  // إجمالي المطلوب
      { width: 16 },  // إجمالي المكتمل
      { width: 14 },  // نسبة الإنجاز
      { width: 14 }   // عدد الفروع
    ];

    // إنشاء شيت لكل قسم
    departmentsData.forEach((department, deptIndex) => {
      const activeBranches = branches.filter(branch => 
        department.products.some(product => 
          product.branchDistribution && product.branchDistribution[branch._id]
        )
      );

      if (activeBranches.length > 0) {
        const deptSheet = workbook.addWorksheet(
          isRtl ? 
            `${department.code.substring(0, 10)}` : 
            `${department.code.substring(0, 10)}`,
          { views: [{ rightToLeft: isRtl }] }
        );

        // عنوان الشيت
        const deptTitleRow = deptSheet.addRow([
          isRtl ? `تقرير قسم ${fixArabicText(department.name)}` : 
          `Department Report: ${department.nameEn || department.name}`
        ]);
        deptTitleRow.height = 22;
        const deptTitleCell = deptTitleRow.getCell(1);
        deptTitleCell.font = { size: 12, bold: true, color: { argb: '374151' } };
        deptTitleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        deptTitleCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FEF3C7' } // amber-100
        };

        // معلومات القسم
        const deptInfoRow = deptSheet.addRow([
          isRtl ? `الكود: ${department.code} | المنتجات: ${department.productsCount} | الفروع: ${activeBranches.length}` :
          `Code: ${department.code} | Products: ${department.productsCount} | Branches: ${activeBranches.length}`
        ]);
        const deptInfoCell = deptInfoRow.getCell(1);
        deptInfoCell.font = { size: 9, color: { argb: '6B7280' } };
        deptInfoCell.alignment = { horizontal: 'center', vertical: 'middle' };

        deptSheet.addRow([]); // سطر فارغ

        // رؤوس أعمدة القسم
        let deptHeaders: string[];
        
        if (isRtl) {
          deptHeaders = [
            'الكود',
            'اسم المنتج',
            ...activeBranches.map(branch => 
              language === 'ar' ? fixArabicText(branch.name) : branch.nameEn
            ),
            'إجمالي المطلوب',
            'إجمالي المكتمل',
            'نسبة الإنجاز'
          ];
        } else {
          deptHeaders = [
            'Code',
            'Product Name',
            ...activeBranches.map(branch => branch.nameEn || branch.name),
            'Total Assigned',
            'Total Completed',
            'Completion Rate'
          ];
        }

        const deptHeaderRow = deptSheet.addRow(deptHeaders);
        deptHeaderRow.height = 22;
        
        deptHeaderRow.eachCell((cell) => {
          cell.font = { bold: true, size: 10, color: { argb: 'FFFFFF' } };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: getRandomColor(deptIndex).replace('bg-', '').replace('-500', '')
          };
          cell.alignment = { 
            horizontal: 'center', 
            vertical: 'middle',
            wrapText: true
          };
          cell.border = {
            top: { style: 'thin', color: { argb: '000000' } },
            left: { style: 'thin', color: { argb: '000000' } },
            bottom: { style: 'thin', color: { argb: '000000' } },
            right: { style: 'thin', color: { argb: '000000' } }
          };
        });

        // بيانات القسم
        department.products.forEach((product, rowIndex) => {
          const rowData = [
            product.code,
            language === 'en' && product.nameEn ? product.nameEn : fixArabicText(product.name),
            ...activeBranches.map(branch => {
              const branchData = product.branchDistribution?.[branch._id];
              return branchData?.assigned || 0;
            }),
            product.totalAssigned,
            product.totalCompleted,
            product.completionRate / 100
          ];
          
          const row = deptSheet.addRow(rowData);
          row.height = 18;
          
          row.eachCell((cell, colNumber) => {
            cell.alignment = { 
              horizontal: 'center', 
              vertical: 'middle',
              wrapText: true
            };
            cell.border = {
              top: { style: 'thin', color: { argb: 'E5E7EB' } },
              left: { style: 'thin', color: { argb: 'E5E7EB' } },
              bottom: { style: 'thin', color: { argb: 'E5E7EB' } },
              right: { style: 'thin', color: { argb: 'E5E7EB' } }
            };
            
            // صفوف متناوبة
            if (rowIndex % 2 === 0) {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'F9FAFB' } // gray-50
              };
            }
            
            // تنسيق الأرقام
            if (colNumber > 2 && colNumber <= deptHeaders.length - 3) {
              cell.numFmt = '#,##0.00';
            } else if (colNumber === deptHeaders.length - 2) {
              cell.numFmt = '#,##0.00';
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FEF3C7' } // amber-100
              };
              cell.font = { color: { argb: '92400E' }, bold: true };
            } else if (colNumber === deptHeaders.length - 1) {
              cell.numFmt = '#,##0.00';
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'D1FAE5' } // emerald-100
              };
              cell.font = { color: { argb: '065F46' }, bold: true };
            } else if (colNumber === deptHeaders.length) {
              cell.numFmt = '0.00%';
            }
          });
        });

        // إجماليات القسم
        const totalBranchValues = activeBranches.map(branch => 
          department.products.reduce((sum, product) => 
            sum + (product.branchDistribution?.[branch._id]?.assigned || 0), 0
          )
        );
        
        const deptTotalRowData = [
          isRtl ? 'إجمالي القسم' : 'Department Total',
          '',
          ...totalBranchValues,
          department.totalAssigned,
          department.totalCompleted,
          department.completionRate / 100
        ];
        
        const deptTotalRow = deptSheet.addRow(deptTotalRowData);
        deptTotalRow.height = 20;
        
        deptTotalRow.eachCell((cell, colNumber) => {
          cell.font = { bold: true, size: 10, color: { argb: 'FFFFFF' } };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: getRandomColor(deptIndex).replace('bg-', '').replace('-500', '')
          };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          cell.border = {
            top: { style: 'thin', color: { argb: '000000' } },
            left: { style: 'thin', color: { argb: '000000' } },
            bottom: { style: 'thin', color: { argb: '000000' } },
            right: { style: 'thin', color: { argb: '000000' } }
          };
          
          if (colNumber > 2 && colNumber <= deptHeaders.length - 3) {
            cell.numFmt = '#,##0.00';
          } else if (colNumber === deptHeaders.length - 2) {
            cell.numFmt = '#,##0.00';
          } else if (colNumber === deptHeaders.length - 1) {
            cell.numFmt = '#,##0.00';
          } else if (colNumber === deptHeaders.length) {
            cell.numFmt = '0.00%';
          }
        });

        // دمج الخلايا
        const deptTotalColumns = 2 + activeBranches.length + 3;
        deptSheet.mergeCells(`A1:${String.fromCharCode(65 + deptTotalColumns - 1)}1`);
        deptSheet.mergeCells(`A2:${String.fromCharCode(65 + deptTotalColumns - 1)}2`);

        // ضبط عرض الأعمدة
        deptSheet.columns = [
          { width: 12 },  // الكود
          { width: 25 },  // اسم المنتج
          ...Array(activeBranches.length).fill({ width: 12 }), // الفروع
          { width: 14 },  // إجمالي المطلوب
          { width: 14 },  // إجمالي المكتمل
          { width: 12 }   // نسبة الإنجاز
        ];
      }
    });

    // حفظ الملف
    const dateStr = new Date().toISOString().split('T')[0];
    const fileName = isRtl 
      ? `تقرير_الأقسام_المفصل_${dateStr}.xlsx`
      : `detailed_departments_report_${dateStr}.xlsx`;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, fileName);

    toast.update(toastId, {
      render: isRtl ? 'تم التصدير إلى Excel بنجاح!' : 'Exported to Excel successfully!',
      type: 'success',
      autoClose: 3000,
    });
  } catch (error) {
    console.error('Excel Export Error:', error);
    toast.update(toastId, {
      render: isRtl ? 'فشل تصدير Excel. حاول مرة أخرى.' : 'Excel export failed. Please try again.',
      type: 'error',
      autoClose: 4000,
    });
  }
};

// ==============================
// === COMPONENTS ===
// ==============================

const Button: React.FC<{
  variant: 'primary' | 'outline' | 'success' | 'amber';
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  icon?: React.ReactNode;
  size?: 'sm' | 'md';
}> = ({ variant, onClick, disabled, children, icon, size = 'md' }) => {
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm'
  };
  
  const variantClasses = {
    amber: 'bg-amber-500 hover:bg-amber-600 text-white shadow-sm hover:shadow',
    primary: 'bg-gray-800 hover:bg-gray-900 text-white shadow-sm hover:shadow',
    success: 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm hover:shadow',
    outline: 'border border-gray-200 hover:bg-gray-50 text-gray-700 hover:border-gray-300'
  };
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center justify-center gap-2 rounded-lg font-medium transition-all ${sizeClasses[size]} ${variantClasses[variant]} ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
      type="button"
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span className="whitespace-nowrap">{children}</span>
    </button>
  );
};

const SearchInput: React.FC<{
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  isRtl: boolean;
}> = ({ value, onChange, placeholder, isRtl }) => {
  return (
    <div className="relative w-full">
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full ${isRtl ? 'pl-10 pr-3' : 'pr-10 pl-3'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-300 focus:border-amber-400 bg-white text-sm ${isRtl ? 'text-right' : 'text-left'} transition-all duration-200`}
        dir={isRtl ? 'rtl' : 'ltr'}
      />
      {value ? (
        <button
          onClick={() => onChange({ target: { value: '' } } as any)}
          className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors`}
          type="button"
        >
          <X className="w-4 h-4" />
        </button>
      ) : (
        <Search className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4`} />
      )}
    </div>
  );
};

const Dropdown: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  ariaLabel: string;
  label?: string;
  icon?: React.ReactNode;
  isRtl: boolean;
}> = ({ value, onChange, options, ariaLabel, label, icon, isRtl }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value) || options[0];

  return (
    <div className="w-full">
      {label && (
        <label className="block text-xs font-medium text-gray-700 mb-1.5" dir={isRtl ? 'rtl' : 'ltr'}>
          {label}
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <button
          dir={isRtl ? 'rtl' : 'ltr'}
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full py-2 px-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-300 focus:border-amber-400 bg-white shadow-sm text-sm font-medium text-gray-700 flex justify-between items-center ${isRtl ? 'text-right' : 'text-left'} transition-all duration-200`}
          aria-label={ariaLabel}
          type="button"
        >
          <div className="flex items-center gap-2 flex-1 truncate" dir={isRtl ? 'rtl' : 'ltr'}>
            {icon && <span className="flex-shrink-0">{icon}</span>}
            <span className="truncate">{selectedOption?.label || ''}</span>
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className={`absolute z-50 w-full mt-1.5 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto font-medium ${isRtl ? 'text-right' : 'text-left'}`}
          >
            {options.map(opt => (
              <button
                key={opt.value}
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-sm ${isRtl ? 'text-right' : 'text-left'} text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors border-b border-gray-100 last:border-b-0`}
                type="button"
                dir={isRtl ? 'rtl' : 'ltr'}
              >
                {opt.label}
              </button>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

const StatsCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: 'gray' | 'emerald' | 'blue' | 'amber';
  loading?: boolean;
  isRtl: boolean;
}> = ({ title, value, icon, color, loading, isRtl }) => {
  const colors = {
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200'
  };

  return (
    <div className={`p-4 rounded-xl border ${colors[color]} transition-all hover:shadow-sm`} dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="flex items-center justify-between mb-3">
        <div className="p-2 rounded-lg bg-white">
          {icon}
        </div>
      </div>
      <div className="mb-2">
        <h3 className="text-sm font-medium opacity-80">{title}</h3>
      </div>
      <div className="text-xl font-bold">
        {loading ? (
          <div className="h-7 w-24 bg-gray-200 rounded animate-pulse"></div>
        ) : (
          value
        )}
      </div>
    </div>
  );
};

const ExpandButton: React.FC<{
  isExpanded: boolean;
  onClick: () => void;
  isRtl: boolean;
}> = ({ isExpanded, onClick, isRtl }) => {
  return (
    <button
      onClick={onClick}
      className={`w-8 h-8 flex items-center justify-center rounded-full bg-amber-100 hover:bg-amber-200 text-amber-600 transition-all`}
      type="button"
    >
      {isExpanded ? (
        <Minus className="w-4 h-4" />
      ) : (
        <Plus className="w-4 h-4" />
      )}
    </button>
  );
};

// مكون زر تصدير القسم الواحد
const DepartmentExportButton: React.FC<{
  department: DepartmentData;
  branches: Branch[];
  periodLabel: string;
  showCompleted: boolean;
  isRtl: boolean;
  language: string;
}> = ({ department, branches, periodLabel, showCompleted, isRtl, language }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleExportPDF = () => {
    exportSingleDepartmentToPDF(
      department,
      branches,
      periodLabel,
      showCompleted,
      isRtl,
      language
    );
    setIsOpen(false);
  };

  const handleExportExcel = () => {
    exportSingleDepartmentToExcel(
      department,
      branches,
      periodLabel,
      showCompleted,
      isRtl,
      language
    );
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
        type="button"
      >
        <Download className="w-3 h-3" />
        {isRtl ? 'تصدير' : 'Export'}
      </button>
      
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.2 }}
          className={`absolute z-50 mt-1.5 ${isRtl ? 'left-0' : 'right-0'} w-40 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden`}
        >
          <button
            onClick={handleExportExcel}
            className="w-full px-4 py-2.5 text-xs text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors border-b border-gray-100 flex items-center gap-2"
            type="button"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" />
            <span>Excel</span>
          </button>
          <button
            onClick={handleExportPDF}
            className="w-full px-4 py-2.5 text-xs text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors flex items-center gap-2"
            type="button"
          >
            <FileText className="w-3.5 h-3.5" />
            <span>PDF</span>
          </button>
        </motion.div>
      )}
    </div>
  );
};

// مكون التصدير الرئيسي
const MainExportDropdown: React.FC<{
  onExportAllPDF: () => void;
  onExportAllExcel: () => void;
  disabled: boolean;
  isRtl: boolean;
}> = ({ onExportAllPDF, onExportAllExcel, disabled, isRtl }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="amber"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        icon={<Download className="w-4 h-4" />}
        size="md"
      >
        {isRtl ? 'تصدير الكل' : 'Export All'}
      </Button>
      
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.2 }}
          className={`absolute z-50 mt-1.5 ${isRtl ? 'left-0' : 'right-0'} w-48 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden`}
        >
          <button
            onClick={() => {
              onExportAllExcel();
              setIsOpen(false);
            }}
            disabled={disabled}
            className="w-full px-4 py-3 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors border-b border-gray-100 flex items-center gap-2"
            type="button"
          >
            <FileSpreadsheet className="w-4 h-4" />
            <span>Excel - جميع الأقسام</span>
          </button>
          <button
            onClick={() => {
              onExportAllPDF();
              setIsOpen(false);
            }}
            disabled={disabled}
            className="w-full px-4 py-3 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors flex items-center gap-2"
            type="button"
          >
            <FileText className="w-4 h-4" />
            <span>PDF - جميع الأقسام</span>
          </button>
        </motion.div>
      )}
    </div>
  );
};

// ==============================
// === MAIN COMPONENT ===
// ==============================
const DepartmentsReport: React.FC = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const isRtl = language === 'ar';
  
  // States
  const [departmentsData, setDepartmentsData] = useState<DepartmentData[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(new Set());
  
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [customDateStart, setCustomDateStart] = useState<string>('');
  const [customDateEnd, setCustomDateEnd] = useState<string>('');
  const [showCompleted, setShowCompleted] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('assigned_desc');
  
  const [totals, setTotals] = useState<ReportTotals>({
    totalDepartments: 0,
    totalProducts: 0,
    totalBranches: 0,
    totalAssigned: 0,
    totalCompleted: 0,
    avgCompletionRate: 0
  });

  // Memoized values
  const periodOptions = useMemo(
    () => PERIOD_OPTIONS.map(p => ({
      value: p.value,
      label: isRtl ? p.labelAr : p.labelEn
    })),
    [isRtl]
  );

  const sortOptions = useMemo(
    () => SORT_OPTIONS.map(s => ({
      value: s.value,
      label: isRtl ? s.labelAr : s.labelEn
    })),
    [isRtl]
  );

  // جلب الفروع
  const fetchBranches = useCallback(async () => {
    try {
      const response = await branchesAPI.getAll();
      const branchesData = response.data || response;
      
      const formattedBranches = Array.isArray(branchesData) ? branchesData.map((branch: any, index: number) => ({
        _id: branch._id?.toString() || branch.id,
        name: branch.name,
        nameEn: branch.nameEn || branch.name,
        code: branch.code || ''
      })) : [];
      
      setBranches(formattedBranches);
      return formattedBranches;
    } catch (error) {
      console.warn('Failed to fetch branches:', error);
      setBranches([]);
      return [];
    }
  }, []);

  // جلب بيانات الأقسام
  const fetchDepartmentsData = useCallback(async () => {
    if (!user || (user.role !== 'admin' && user.role !== 'production' && user.role !== 'accountant' && user.role !== 'chef')) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setStatsLoading(true);

    try {
      const params: any = {
        lang: language,
        includeProducts: 'true',
        includeBranches: 'true'
      };
      
      if (period === 'custom' && customDateStart && customDateEnd) {
        params.startDate = customDateStart;
        params.endDate = customDateEnd;
        setStartDate(customDateStart);
        setEndDate(customDateEnd);
      } else {
        const { start, end } = calculatePeriodDates(period);
        params.startDate = format(start, 'yyyy-MM-dd');
        params.endDate = format(end, 'yyyy-MM-dd');
        setStartDate(params.startDate);
        setEndDate(params.endDate);
      }
      
      const response = await reportsAPI.getDepartmentsReport(params);
      
      let departments: DepartmentData[] = [];
      
      if (response.success && response.data) {
        departments = response.data;
      } else if (Array.isArray(response.data)) {
        departments = response.data;
      } else if (Array.isArray(response)) {
        departments = response;
      }
      
      setDepartmentsData(departments);
      
      // حساب الإحصائيات
      const totalProducts = departments.reduce((sum, dept) => sum + (dept.productsCount || 0), 0);
      const totalAssigned = departments.reduce((sum, dept) => sum + (dept.totalAssigned || 0), 0);
      const totalCompleted = departments.reduce((sum, dept) => sum + (dept.totalCompleted || 0), 0);
      const activeBranches = new Set<string>();
      
      departments.forEach(dept => {
        dept.branches?.forEach(branch => {
          if (branch.assigned > 0) {
            activeBranches.add(branch.id);
          }
        });
      });
      
      const avgCompletionRate = departments.length > 0 
        ? departments.reduce((sum, dept) => sum + (dept.completionRate || 0), 0) / departments.length 
        : 0;
      
      setTotals({
        totalDepartments: departments.length,
        totalProducts,
        totalBranches: activeBranches.size,
        totalAssigned,
        totalCompleted,
        avgCompletionRate
      });
      
    } catch (error: any) {
      console.error('Error fetching departments data:', error);
      toast.error(error.message || (isRtl ? 'فشل في تحميل البيانات' : 'Failed to load data'), {
        position: isRtl ? 'top-left' : 'top-right'
      });
      
      // بيانات تجريبية
      const mockDepartments: DepartmentData[] = [
        {
          id: '1',
          code: 'D001',
          name: 'البسبوسة',
          nameAr: 'البسبوسة',
          products: [
            {
              id: '1',
              code: 'B2-KITKAT',
              name: 'بسبوسة كيت كات',
              nameAr: 'بسبوسة كيت كات',
              unit: 'كيلو',
              totalAssigned: 33072.50,
              totalCompleted: 33016,
              totalAmount: 33072.50,
              completionRate: 99.83,
              branchDistribution: {
                '1': { assigned: 74, completed: 59 },
                '2': { assigned: 65, completed: 52 }
              }
            }
          ],
          productsCount: 1,
          totalAssigned: 33072.50,
          totalCompleted: 33016,
          totalAmount: 33072.50,
          completionRate: 99.83,
          orderCount: 45,
          branches: [
            { 
              id: '1', 
              name: 'الاسياح', 
              nameEn: 'Al Asyah', 
              code: 'B001', 
              assigned: 253, 
              completed: 218, 
              amount: 2530, 
              orderCount: 8, 
              completionRate: 86.17 
            }
          ]
        }
      ];
      
      setDepartmentsData(mockDepartments);
      
      setTotals({
        totalDepartments: mockDepartments.length,
        totalProducts: mockDepartments.reduce((sum, d) => sum + d.productsCount, 0),
        totalBranches: 1,
        totalAssigned: mockDepartments.reduce((sum, d) => sum + d.totalAssigned, 0),
        totalCompleted: mockDepartments.reduce((sum, d) => sum + d.totalCompleted, 0),
        avgCompletionRate: 99.83
      });
      
    } finally {
      setLoading(false);
      setStatsLoading(false);
    }
  }, [period, customDateStart, customDateEnd, language, user, isRtl]);

  // Initial fetch
  useEffect(() => {
    const initialize = async () => {
      await fetchBranches();
      
      if (period !== 'custom') {
        const { start, end } = calculatePeriodDates(period);
        setStartDate(format(start, 'yyyy-MM-dd'));
        setEndDate(format(end, 'yyyy-MM-dd'));
      }
      
      await fetchDepartmentsData();
    };
    
    initialize();
  }, [fetchBranches]);

  // Fetch data when period changes
  useEffect(() => {
    if (period !== 'custom') {
      fetchDepartmentsData();
    }
  }, [period, fetchDepartmentsData]);

  // Filter and sort data
  const filteredAndSortedDepartments = useMemo(() => {
    let filtered = departmentsData;
    
    // تصفية حسب البحث
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(dept => 
        dept.name?.toLowerCase().includes(query) ||
        dept.nameAr?.toLowerCase().includes(query) ||
        dept.code?.toLowerCase().includes(query) ||
        dept.products?.some(p => 
          p.name?.toLowerCase().includes(query) ||
          p.code?.toLowerCase().includes(query)
        )
      );
    }
    
    // الترتيب
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name_asc':
          return fixArabicText(a.name || '').localeCompare(fixArabicText(b.name || ''));
        case 'name_desc':
          return fixArabicText(b.name || '').localeCompare(fixArabicText(a.name || ''));
        case 'assigned_desc':
          return (b.totalAssigned || 0) - (a.totalAssigned || 0);
        case 'completed_desc':
          return (b.totalCompleted || 0) - (a.totalCompleted || 0);
        case 'rate_desc':
          return (b.completionRate || 0) - (a.completionRate || 0);
        default:
          return (b.totalAssigned || 0) - (a.totalAssigned || 0);
      }
    });
    
    return filtered;
  }, [departmentsData, searchQuery, sortBy]);

  // Event handlers
  const handleRefresh = useCallback(() => {
    fetchDepartmentsData();
    toast.success(isRtl ? 'تم تحديث البيانات' : 'Data refreshed successfully');
  }, [fetchDepartmentsData, isRtl]);

  const toggleDepartment = useCallback((departmentId: string) => {
    setExpandedDepartments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(departmentId)) {
        newSet.delete(departmentId);
      } else {
        newSet.add(departmentId);
      }
      return newSet;
    });
  }, []);

  const handleExportAllPDF = useCallback(() => {
    exportAllDepartmentsToPDF(
      filteredAndSortedDepartments,
      branches,
      period === 'custom' ? `${customDateStart} - ${customDateEnd}` : periodOptions.find(p => p.value === period)?.label || '',
      showCompleted,
      isRtl,
      language
    );
  }, [filteredAndSortedDepartments, branches, period, customDateStart, customDateEnd, periodOptions, showCompleted, isRtl, language]);

  const handleExportAllExcel = useCallback(() => {
    exportAllDepartmentsToExcel(
      filteredAndSortedDepartments,
      branches,
      period === 'custom' ? `${customDateStart} - ${customDateEnd}` : periodOptions.find(p => p.value === period)?.label || '',
      showCompleted,
      isRtl,
      language
    );
  }, [filteredAndSortedDepartments, branches, period, customDateStart, customDateEnd, periodOptions, showCompleted, isRtl, language]);

  const applyCustomDate = useCallback(() => {
    if (!customDateStart || !customDateEnd) {
      toast.warning(isRtl ? 'الرجاء تحديد تاريخ البداية والنهاية' : 'Please select start and end dates');
      return;
    }
    
    if (new Date(customDateEnd) < new Date(customDateStart)) {
      toast.error(isRtl ? 'تاريخ النهاية يجب أن يكون بعد تاريخ البداية' : 'End date must be after start date');
      return;
    }
    
    fetchDepartmentsData();
  }, [customDateStart, customDateEnd, fetchDepartmentsData, isRtl]);

  const periodLabel = useMemo(() => {
    if (period === 'custom' && customDateStart && customDateEnd) {
      return `${customDateStart} - ${customDateEnd}`;
    }
    return periodOptions.find(p => p.value === period)?.label || '';
  }, [period, customDateStart, customDateEnd, periodOptions]);

  // عرض جدول المنتجات لكل قسم مع تحسين التنسيق للعربية
  const renderDepartmentProductsTable = (department: DepartmentData) => {
    const activeBranches = branches.filter(branch => 
      department.products.some(product => 
        product.branchDistribution && product.branchDistribution[branch._id]
      )
    );

    return (
      <div className="mt-6">
      
        
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto max-w-full">
            <table className="min-w-full divide-y divide-gray-200 text-xs" dir={isRtl ? 'rtl' : 'ltr'}>
              <thead>
                <tr className="bg-amber-50">
                  {isRtl ? (
                    <>
                      {/* للعربية: ترتيب من اليمين لليسار */}
                      <th className="px-3 py-3 text-gray-700 text-center sticky right-0 bg-amber-50 z-20 min-w-[50px]">
                        #
                      </th>
                      <th className="px-3 py-3 text-gray-700 text-center sticky right-[50px] bg-amber-50 z-20 min-w-[100px]">
                        {isRtl ? 'الكود' : 'Code'}
                      </th>
                      <th className="px-3 py-3 text-gray-700 text-center sticky right-[150px] bg-amber-50 z-20 min-w-[180px]">
                        {isRtl ? 'اسم المنتج' : 'Product Name'}
                      </th>
                      <th className="px-3 py-3 text-gray-700 text-center min-w-[80px]">
                        {isRtl ? 'الوحدة' : 'Unit'}
                      </th>
                      
                      {/* أعمدة الفروع - من اليمين لليسار */}
                      {[...activeBranches].reverse().map((branch, index) => (
                        <th
                          key={branch._id}
                          className="px-2 py-3 text-gray-700 text-center min-w-[90px] max-w-[110px]"
                        >
                          <div className="flex flex-col items-center">
                            <div className="flex items-center gap-1 mb-1">
                              <div className={`w-2 h-2 rounded-full ${getRandomColor(index)}`}></div>
                              <span className="text-[11px] font-medium truncate max-w-[70px]" title={isRtl ? fixArabicText(branch.name) : branch.nameEn}>
                                {isRtl ? fixArabicText(branch.name) : branch.nameEn}
                              </span>
                            </div>
                            <span className="text-[9px] text-gray-500">
                              #{index + 1}
                            </span>
                          </div>
                        </th>
                      ))}
                      
                      {/* الأعمدة الإجمالية - من اليمين لليسار */}
                      <th className="px-3 py-3 text-gray-700 text-center min-w-[110px] bg-amber-100">
                        {isRtl ? 'إجمالي المطلوب' : 'Total Assigned'}
                      </th>
                      <th className="px-3 py-3 text-gray-700 text-center min-w-[110px] bg-emerald-100">
                        {isRtl ? 'إجمالي المكتمل' : 'Total Completed'}
                      </th>
                      <th className="px-3 py-3 text-gray-700 text-center min-w-[110px] bg-blue-100">
                        {isRtl ? 'نسبة الإنجاز' : 'Completion Rate'}
                      </th>
                    </>
                  ) : (
                    <>
                      {/* للإنجليزية: ترتيب من اليسار لليمين */}
                      <th className="px-3 py-3 text-gray-700 text-center sticky left-0 bg-amber-50 z-20 min-w-[50px]">
                        #
                      </th>
                      <th className="px-3 py-3 text-gray-700 text-center sticky left-[50px] bg-amber-50 z-20 min-w-[100px]">
                        {isRtl ? 'الكود' : 'Code'}
                      </th>
                      <th className="px-3 py-3 text-gray-700 text-center sticky left-[150px] bg-amber-50 z-20 min-w-[180px]">
                        {isRtl ? 'اسم المنتج' : 'Product Name'}
                      </th>
                      <th className="px-3 py-3 text-gray-700 text-center min-w-[80px]">
                        {isRtl ? 'الوحدة' : 'Unit'}
                      </th>
                      
                      {/* أعمدة الفروع */}
                      {activeBranches.map((branch, index) => (
                        <th
                          key={branch._id}
                          className="px-2 py-3 text-gray-700 text-center min-w-[90px] max-w-[110px]"
                        >
                          <div className="flex flex-col items-center">
                            <div className="flex items-center gap-1 mb-1">
                              <div className={`w-2 h-2 rounded-full ${getRandomColor(index)}`}></div>
                              <span className="text-[11px] font-medium truncate max-w-[70px]" title={isRtl ? fixArabicText(branch.name) : branch.nameEn}>
                                {isRtl ? fixArabicText(branch.name) : branch.nameEn}
                              </span>
                            </div>
                            <span className="text-[9px] text-gray-500">
                              #{index + 1}
                            </span>
                          </div>
                        </th>
                      ))}
                      
                      {/* الأعمدة الإجمالية */}
                      <th className="px-3 py-3 text-gray-700 text-center min-w-[110px] bg-amber-100">
                        {isRtl ? 'إجمالي المطلوب' : 'Total Assigned'}
                      </th>
                      <th className="px-3 py-3 text-gray-700 text-center min-w-[110px] bg-emerald-100">
                        {isRtl ? 'إجمالي المكتمل' : 'Total Completed'}
                      </th>
                      <th className="px-3 py-3 text-gray-700 text-center min-w-[110px] bg-blue-100">
                        {isRtl ? 'نسبة الإنجاز' : 'Completion Rate'}
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {department.products.map((product, idx) => {
                  const productBranchTotals = activeBranches.reduce((totals, branch) => {
                    const branchData = product.branchDistribution?.[branch._id];
                    if (branchData) {
                      totals.assigned += branchData.assigned || 0;
                      totals.completed += branchData.completed || 0;
                    }
                    return totals;
                  }, { assigned: 0, completed: 0 });
                  
                  const completionRate = productBranchTotals.assigned > 0 
                    ? (productBranchTotals.completed / productBranchTotals.assigned) * 100 
                    : 0;
                  
                  return (
                    <tr key={product.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'} hover:bg-gray-50`}>
                      {isRtl ? (
                        <>
                          {/* للعربية: خلايا من اليمين لليسار */}
                          <td className="px-3 py-2.5 text-center text-gray-900 sticky right-0 bg-white">
                            {idx + 1}
                          </td>
                          <td className="px-3 py-2.5 text-center text-gray-900 sticky right-[50px] bg-white">
                            <span className="font-mono bg-gray-100 px-2 py-1 rounded text-xs">
                              {product.code}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-center text-gray-900 sticky right-[150px] bg-white">
                            <span className="text-sm">{fixArabicText(product.name)}</span>
                          </td>
                          <td className="px-3 py-2.5 text-center text-gray-700">
                            <span className="text-sm">{fixArabicText(product.unit)}</span>
                          </td>
                          
                          {/* بيانات الفروع - عكس الترتيب للعربية */}
                          {[...activeBranches].reverse().map((branch) => {
                            const branchData = product.branchDistribution?.[branch._id];
                            const assigned = branchData?.assigned || 0;
                            const completed = branchData?.completed || 0;
                            
                            return (
                              <td
                                key={`${product.id}-${branch._id}`}
                                className="px-2 py-2.5 text-center"
                              >
                                {assigned === 0 && completed === 0 ? (
                                  <span className="text-sm text-gray-400">-</span>
                                ) : (
                                  <div className="space-y-0.5">
                                    <div className={`font-medium text-sm ${getCompletionRateColor(completionRate)}`}>
                                      {formatNumber(assigned, isRtl)}
                                    </div>
                                    {showCompleted && completed > 0 && (
                                      <div className="text-[11px] text-emerald-600">
                                        ({formatNumber(completed, isRtl)})
                                      </div>
                                    )}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                          
                          <td className="px-3 py-2.5 text-center text-sm font-bold text-amber-700 bg-amber-50">
                            {formatNumber(productBranchTotals.assigned, isRtl)}
                          </td>
                          <td className="px-3 py-2.5 text-center text-sm font-bold text-emerald-700 bg-emerald-50">
                            {formatNumber(productBranchTotals.completed, isRtl)}
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getCompletionRateColor(completionRate)}`}>
                              {formatPercentage(completionRate, isRtl)}
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          {/* للإنجليزية: خلايا من اليسار لليمين */}
                          <td className="px-3 py-2.5 text-center text-gray-900 sticky left-0 bg-white">
                            {idx + 1}
                          </td>
                          <td className="px-3 py-2.5 text-center text-gray-900 sticky left-[50px] bg-white">
                            <span className="font-mono bg-gray-100 px-2 py-1 rounded text-xs">
                              {product.code}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-center text-gray-900 sticky left-[150px] bg-white">
                            <span className="text-sm">
                              {language === 'en' && product.nameEn ? product.nameEn : fixArabicText(product.name)}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-center text-gray-700">
                            <span className="text-sm">{fixArabicText(product.unit)}</span>
                          </td>
                          
                          {/* بيانات الفروع */}
                          {activeBranches.map((branch) => {
                            const branchData = product.branchDistribution?.[branch._id];
                            const assigned = branchData?.assigned || 0;
                            const completed = branchData?.completed || 0;
                            
                            return (
                              <td
                                key={`${product.id}-${branch._id}`}
                                className="px-2 py-2.5 text-center"
                              >
                                {assigned === 0 && completed === 0 ? (
                                  <span className="text-sm text-gray-400">-</span>
                                ) : (
                                  <div className="space-y-0.5">
                                    <div className={`font-medium text-sm ${getCompletionRateColor(completionRate)}`}>
                                      {formatNumber(assigned, isRtl)}
                                    </div>
                                    {showCompleted && completed > 0 && (
                                      <div className="text-[11px] text-emerald-600">
                                        ({formatNumber(completed, isRtl)})
                                      </div>
                                    )}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                          
                          <td className="px-3 py-2.5 text-center text-sm font-bold text-amber-700 bg-amber-50">
                            {formatNumber(productBranchTotals.assigned, isRtl)}
                          </td>
                          <td className="px-3 py-2.5 text-center text-sm font-bold text-emerald-700 bg-emerald-50">
                            {formatNumber(productBranchTotals.completed, isRtl)}
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getCompletionRateColor(completionRate)}`}>
                              {formatPercentage(completionRate, isRtl)}
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
                
                {/* صف الإجماليات */}
                <tr className="bg-gray-800 text-white font-bold">
                  {isRtl ? (
                    <>
                      <td className="px-3 py-2.5 text-center sticky right-0 bg-gray-800" colSpan={4}>
                        {isRtl ? 'إجمالي القسم' : 'Department Total'}
                      </td>
                      
                      {/* إجماليات الفروع - عكس الترتيب */}
                      {[...activeBranches].reverse().map((branch) => {
                        const branchTotal = department.products.reduce((sum, product) => {
                          const branchData = product.branchDistribution?.[branch._id];
                          return sum + (branchData?.assigned || 0);
                        }, 0);
                        
                        return (
                          <td
                            key={`total-${branch._id}`}
                            className="px-2 py-2.5 text-center font-medium bg-gray-700"
                          >
                            <span className="text-sm">
                              {formatNumber(branchTotal, isRtl)}
                            </span>
                          </td>
                        );
                      })}
                      
                      <td className="px-3 py-2.5 text-center font-medium bg-amber-600">
                        {formatNumber(department.totalAssigned, isRtl)}
                      </td>
                      <td className="px-3 py-2.5 text-center font-medium bg-emerald-600">
                        {formatNumber(department.totalCompleted, isRtl)}
                      </td>
                      <td className="px-3 py-2.5 text-center font-medium bg-blue-600">
                        {formatPercentage(department.completionRate, isRtl)}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2.5 text-center sticky left-0 bg-gray-800" colSpan={4}>
                        {isRtl ? 'إجمالي القسم' : 'Department Total'}
                      </td>
                      
                      {/* إجماليات الفروع */}
                      {activeBranches.map((branch) => {
                        const branchTotal = department.products.reduce((sum, product) => {
                          const branchData = product.branchDistribution?.[branch._id];
                          return sum + (branchData?.assigned || 0);
                        }, 0);
                        
                        return (
                          <td
                            key={`total-${branch._id}`}
                            className="px-2 py-2.5 text-center font-medium bg-gray-700"
                          >
                            <span className="text-sm">
                              {formatNumber(branchTotal, isRtl)}
                            </span>
                          </td>
                        );
                      })}
                      
                      <td className="px-3 py-2.5 text-center font-medium bg-amber-600">
                        {formatNumber(department.totalAssigned, isRtl)}
                      </td>
                      <td className="px-3 py-2.5 text-center font-medium bg-emerald-600">
                        {formatNumber(department.totalCompleted, isRtl)}
                      </td>
                      <td className="px-3 py-2.5 text-center font-medium bg-blue-600">
                        {formatPercentage(department.completionRate, isRtl)}
                      </td>
                    </>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // عرض القائمة الرئيسية للأقسام
  const renderDepartmentsList = () => {
    if (filteredAndSortedDepartments.length === 0) {
      return (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {isRtl ? 'لا توجد بيانات للأقسام' : 'No departments data found'}
          </h3>
          <p className="text-gray-600">
            {isRtl ? 'جرب تغيير الفترة الزمنية أو معايير البحث' : 'Try changing the time period or search criteria'}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {filteredAndSortedDepartments.map((department) => {
          const isExpanded = expandedDepartments.has(department.id);
          const activeBranches = department.branches?.filter(b => b.assigned > 0).length || 0;
          
          return (
            <div key={department.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Department Header */}
              <div className="px-6 py-4 bg-gradient-to-r from-amber-50 to-amber-100 border-b border-amber-200">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {fixArabicText(department.name)}
                        </h3>
                        <span className="text-xs px-2 py-1 bg-amber-100 text-amber-800 rounded">
                          {department.code}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        {department.productsCount} {isRtl ? 'منتج' : 'product'} • 
                        {activeBranches} {isRtl ? 'فرع نشط' : 'active branch'} • 
                        {formatPercentage(department.completionRate, isRtl)} {isRtl ? 'نسبة الإنجاز' : 'completion rate'}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-lg font-bold text-amber-600">
                          {formatNumber(department.totalAssigned, isRtl)}
                        </div>
                        <div className="text-xs text-gray-600">
                          {isRtl ? 'المطلوب' : 'Assigned'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-emerald-600">
                          {formatNumber(department.totalCompleted, isRtl)}
                        </div>
                        <div className="text-xs text-gray-600">
                          {isRtl ? 'المكتمل' : 'Completed'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <DepartmentExportButton
                        department={department}
                        branches={branches}
                        periodLabel={periodLabel}
                        showCompleted={showCompleted}
                        isRtl={isRtl}
                        language={language}
                      />
                      <ExpandButton
                        isExpanded={isExpanded}
                        onClick={() => toggleDepartment(department.id)}
                        isRtl={isRtl}
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Expanded Content - جدول المنتجات */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    {renderDepartmentProductsTable(department)}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    );
  };

  if (!user || (user.role !== 'admin' && user.role !== 'production' && user.role !== 'accountant' && user.role !== 'chef')) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
            <X className="w-4 h-4 text-red-600" />
          </div>
          <span className="text-sm text-red-600">{isRtl ? 'غير مصرح لك للوصول إلى هذه الصفحة' : 'Not authorized to access this page'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-6 mx-auto max-w-[99vw]" dir={isRtl ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="w-full mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl shadow-lg hidden md:flex">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isRtl ? 'تقرير الأقسام' : 'Departments Report'}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {isRtl 
                  ? 'عرض المنتجات المطلوبة لكل قسم مع توزيعها على الفروع' 
                  : 'View requested products per department with branch distribution'
                }
              </p>
            </div>
          </div>
          
          {/* Header Actions */}
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleRefresh}
              icon={<RefreshCw className="w-4 h-4" />}
              size="md"
            >
              {isRtl ? 'تحديث' : 'Refresh'}
            </Button>
            
            <MainExportDropdown
              onExportAllPDF={handleExportAllPDF}
              onExportAllExcel={handleExportAllExcel}
              disabled={filteredAndSortedDepartments.length === 0}
              isRtl={isRtl}
            />
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
          <StatsCard
            title={isRtl ? 'إجمالي الأقسام' : 'Total Departments'}
            value={statsLoading ? '...' : formatNumber(totals.totalDepartments, isRtl)}
            icon={<Building2 className="w-5 h-5" />}
            color="gray"
            loading={statsLoading}
            isRtl={isRtl}
          />
          <StatsCard
            title={isRtl ? 'إجمالي المنتجات' : 'Total Products'}
            value={statsLoading ? '...' : formatNumber(totals.totalProducts, isRtl)}
            icon={<Package className="w-5 h-5" />}
            color="emerald"
            loading={statsLoading}
            isRtl={isRtl}
          />
          <StatsCard
            title={isRtl ? 'الفروع النشطة' : 'Active Branches'}
            value={statsLoading ? '...' : formatNumber(totals.totalBranches, isRtl)}
            icon={<Store className="w-5 h-5" />}
            color="blue"
            loading={statsLoading}
            isRtl={isRtl}
          />
          <StatsCard
            title={isRtl ? 'إجمالي المطلوب' : 'Total Assigned'}
            value={statsLoading ? '...' : formatNumber(totals.totalAssigned, isRtl)}
            icon={<Hash className="w-5 h-5" />}
            color="amber"
            loading={statsLoading}
            isRtl={isRtl}
          />
          <StatsCard
            title={isRtl ? 'إجمالي المكتمل' : 'Total Completed'}
            value={statsLoading ? '...' : formatNumber(totals.totalCompleted, isRtl)}
            icon={<CheckCircle className="w-5 h-5" />}
            color="emerald"
            loading={statsLoading}
            isRtl={isRtl}
          />
          <StatsCard
            title={isRtl ? 'متوسط نسبة الإنجاز' : 'Avg Completion Rate'}
            value={statsLoading ? '...' : formatPercentage(totals.avgCompletionRate, isRtl)}
            icon={<TrendingUp className="w-5 h-5" />}
            color="blue"
            loading={statsLoading}
            isRtl={isRtl}
          />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-600" />
            {isRtl ? 'الفلاتر والبحث' : 'Filters & Search'}
          </h3>
          
          {/* Search and Period in one row on large screens */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {/* Search Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {isRtl ? 'بحث في الأقسام والمنتجات' : 'Search Departments & Products'}
              </label>
              <SearchInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={isRtl ? 'ابحث باسم القسم، المنتج، أو الكود...' : 'Search by department, product, or code...'}
                isRtl={isRtl}
              />
            </div>

            {/* Period Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {isRtl ? 'الفترة' : 'Period'}
              </label>
              <Dropdown
                value={period}
                onChange={setPeriod}
                options={periodOptions}
                ariaLabel={isRtl ? 'اختر الفترة' : 'Select period'}
                icon={<Calendar className="w-4 h-4" />}
                isRtl={isRtl}
              />
            </div>
          </div>

          {/* Sort and Custom Dates */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Sort */}
            <Dropdown
              value={sortBy}
              onChange={setSortBy}
              options={sortOptions}
              ariaLabel={isRtl ? 'اختر طريقة الترتيب' : 'Select sorting method'}
              label={isRtl ? 'الترتيب' : 'Sort By'}
              icon={<SortAsc className="w-4 h-4" />}
              isRtl={isRtl}
            />

            {/* Custom Dates */}
            {period === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {isRtl ? 'من تاريخ' : 'From Date'}
                  </label>
                  <input
                    type="date"
                    value={customDateStart}
                    onChange={(e) => setCustomDateStart(e.target.value)}
                    className="w-full py-2 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-300 focus:border-amber-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {isRtl ? 'إلى تاريخ' : 'To Date'}
                  </label>
                  <input
                    type="date"
                    value={customDateEnd}
                    onChange={(e) => setCustomDateEnd(e.target.value)}
                    className="w-full py-2 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-300 focus:border-amber-400"
                  />
                </div>
              </>
            )}
          </div>

          {/* Apply Custom Date Button */}
          <div className="flex flex-wrap items-center justify-between gap-4 mt-4 pt-4 border-t border-gray-100">
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <span className={`text-sm text-gray-700`}>
                  {isRtl ? 'عرض الكميات المكتملة' : 'Show Completed Quantities'}
                </span>
                <button
                  type="button"
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-300 ${
                    showCompleted ? 'bg-emerald-500' : 'bg-gray-300'
                  }`}
                  onClick={() => setShowCompleted(!showCompleted)}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                      showCompleted ? (isRtl ? 'translate-x-1' : 'translate-x-6') : (isRtl ? 'translate-x-6' : 'translate-x-1')
                    }`}
                  />
                </button>
              </div>
            </div>
            
            {period === 'custom' && (
              <Button
                variant="amber"
                onClick={applyCustomDate}
                disabled={!customDateStart || !customDateEnd}
                size="md"
              >
                {isRtl ? 'تطبيق الفترة' : 'Apply Period'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Report Header Info */}
      {!loading && filteredAndSortedDepartments.length > 0 && (
        <div className="mb-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
          <div className="flex flex-wrap justify-center items-center gap-4 text-sm">
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-amber-200">
              <Calendar className="w-4 h-4 text-amber-600" />
              <span className="font-medium text-gray-700">{periodLabel}</span>
            </div>
            
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-amber-200">
              <SortAsc className="w-4 h-4 text-amber-600" />
              <span className="font-medium text-gray-700">
                {isRtl ? 'الترتيب: ' : 'Sort by: '}
                {sortOptions.find(s => s.value === sortBy)?.label || ''}
              </span>
            </div>
            
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-amber-200">
              <Info className="w-4 h-4 text-amber-600" />
              <span className="font-medium text-gray-700">
                {isRtl ? 'عرض: ' : 'Show: '}
                {showCompleted ? (isRtl ? 'المكتمل' : 'Completed') : (isRtl ? 'المطلوب فقط' : 'Assigned Only')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-500 mx-auto mb-4"></div>
            <p className="text-sm text-gray-600">{isRtl ? 'جارٍ تحميل البيانات...' : 'Loading data...'}</p>
          </div>
        </div>
      ) : filteredAndSortedDepartments.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="inline-block p-3 bg-amber-100 rounded-full mb-3">
            <Search className="w-6 h-6 text-amber-600" />
          </div>
          <h3 className="text-base font-semibold text-gray-700 mb-2">{isRtl ? 'لا توجد بيانات' : 'No data found'}</h3>
          <p className="text-gray-600 text-sm mb-4">
            {isRtl 
              ? 'لا توجد بيانات مطابقة لبحثك.'
              : 'No data matches your search.'
            }
          </p>
          <Button
            variant="amber"
            onClick={handleRefresh}
            icon={<RefreshCw className="w-4 h-4" />}
            size="sm"
          >
            {isRtl ? 'تحديث البيانات' : 'Refresh Data'}
          </Button>
        </div>
      ) : (
        <>
          {renderDepartmentsList()}
          
          {/* Footer Summary */}
          {filteredAndSortedDepartments.length > 0 && (
            <div className="mt-6 p-4 bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl text-white">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold">
                    {isRtl ? 'ملخص النتائج' : 'Results Summary'}
                  </h3>
                  <p className="text-amber-100 text-sm">
                    {isRtl 
                      ? `تم تحليل ${formatNumber(filteredAndSortedDepartments.length, isRtl)} قسم، ${formatNumber(totals.totalProducts, isRtl)} منتج، و ${formatNumber(totals.totalBranches, isRtl)} فرع`
                      : `Analyzed ${formatNumber(filteredAndSortedDepartments.length, isRtl)} departments, ${formatNumber(totals.totalProducts, isRtl)} products, and ${formatNumber(totals.totalBranches, isRtl)} branches`
                    }
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold">
                      {formatNumber(totals.totalAssigned, isRtl)}
                    </div>
                    <div className="text-sm text-amber-100">
                      {isRtl ? 'إجمالي المطلوب' : 'Total Assigned'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold">
                      {formatNumber(totals.totalCompleted, isRtl)}
                    </div>
                    <div className="text-sm text-amber-100">
                      {isRtl ? 'إجمالي المكتمل' : 'Total Completed'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold">
                      {formatPercentage(totals.avgCompletionRate, isRtl)}
                    </div>
                    <div className="text-sm text-amber-100">
                      {isRtl ? 'متوسط نسبة الإنجاز' : 'Avg Completion Rate'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DepartmentsReport;